import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

interface Product {
  sku: string;
  asin: string | null;
  nickname: string | null;
  parentNickname: string | null;
  title: string | null;
  imageUrl: string | null;
  marketplaces: string[];
}

interface Optimization {
  asin: string;
  marketplace: string;
  currentTitle: string | null;
  optimizedTitle: string;
  bullets: string[];
  description: string;
  backendKeywords: string[];
  improvements: string[];
  score: number;
}

const MP_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX', A1F83G8C2ARO7P: 'UK',
};

export function ListingOptimizer() {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedAsin, setSelectedAsin] = useState('');
  const [selectedMp, setSelectedMp] = useState('ATVPDKIKX0DER');
  const [optimization, setOptimization] = useState<Optimization | null>(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<{ products: Product[] }>('/inventory/products')
      .then(res => {
        const withAsin = res.products.filter(p => p.asin);
        setProducts(withAsin);
        if (withAsin.length > 0) setSelectedAsin(withAsin[0].asin!);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleOptimize = async () => {
    if (!selectedAsin) return;
    setOptimizing(true); setError(''); setOptimization(null);
    try {
      const res = await api.post<{ ok: boolean; optimization: Optimization; error?: string }>('/reviews/optimize-listing', {
        asin: selectedAsin, marketplace: selectedMp,
      });
      if (res.optimization) setOptimization(res.optimization);
      else setError(res.error ?? 'No result returned');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  const selected = products.find(p => p.asin === selectedAsin);

  if (loading) return <div className="space-y-4 animate-pulse"><div className="h-10 w-64 rounded bg-gray-200" /><div className="h-64 rounded-xl bg-gray-200" /></div>;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold">AI Listing Optimizer</h2>
        <p className="text-sm text-gray-500">Generate optimized titles, bullets, and descriptions using AI analysis of reviews, keywords, and competitors</p>
      </div>

      {/* Product selector */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Product</label>
          <select value={selectedAsin} onChange={e => setSelectedAsin(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm min-w-[300px]">
            {products.map(p => {
              const label = p.parentNickname && p.nickname && p.nickname !== p.parentNickname
                ? `${p.parentNickname} — ${p.nickname}`
                : p.nickname ?? p.parentNickname ?? p.title?.slice(0, 50) ?? p.asin;
              return <option key={p.asin} value={p.asin!}>{label}</option>;
            })}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Market</label>
          <select value={selectedMp} onChange={e => setSelectedMp(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            {(selected?.marketplaces ?? ['ATVPDKIKX0DER']).map(mp => (
              <option key={mp} value={mp}>{MP_LABELS[mp] ?? mp}</option>
            ))}
          </select>
        </div>
        <button onClick={handleOptimize} disabled={optimizing || !selectedAsin}
          className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
          {optimizing ? 'Analyzing...' : 'Optimize Listing'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      {optimizing && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/30 p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
          <p className="mt-3 text-sm text-gray-600">AI is analyzing reviews, keywords, and competitors...</p>
          <p className="text-xs text-gray-400">This takes 10-20 seconds</p>
        </div>
      )}

      {optimization && (
        <div className="space-y-4">
          {/* Score */}
          <div className="flex items-center gap-4">
            <div className={`rounded-xl border p-4 text-center ${
              optimization.score >= 80 ? 'border-green-200 bg-green-50' : optimization.score >= 60 ? 'border-yellow-200 bg-yellow-50' : 'border-red-200 bg-red-50'
            }`}>
              <p className={`text-3xl font-bold ${optimization.score >= 80 ? 'text-green-600' : optimization.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                {optimization.score}
              </p>
              <p className="text-xs text-gray-500">Quality Score</p>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Improvements</h3>
              <ul className="mt-1 space-y-1">
                {optimization.improvements.map((imp, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                    <span className="mt-0.5 text-green-500">+</span> {imp}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Title comparison */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
            <h3 className="text-sm font-semibold">Title</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-[10px] font-medium text-gray-400 uppercase mb-1">Current</p>
                <p className="text-sm text-gray-700">{optimization.currentTitle ?? '—'}</p>
              </div>
              <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                <p className="text-[10px] font-medium text-green-600 uppercase mb-1">Optimized</p>
                <p className="text-sm text-gray-900 font-medium">{optimization.optimizedTitle}</p>
              </div>
            </div>
          </div>

          {/* Bullets */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold mb-3">Bullet Points</h3>
            <div className="space-y-2">
              {optimization.bullets.map((bullet, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <span className="flex-shrink-0 rounded-full bg-blue-200 text-blue-700 h-5 w-5 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <p className="text-sm text-gray-800">{bullet}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold mb-2">Product Description</h3>
            <p className="text-sm text-gray-700 leading-relaxed">{optimization.description}</p>
          </div>

          {/* Backend Keywords */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold mb-2">Backend Keywords</h3>
            <div className="flex flex-wrap gap-1.5">
              {optimization.backendKeywords.map((kw, i) => (
                <span key={i} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{kw}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
